// Loads a TTF font that supports full Turkish character set into jsPDF.
// Roboto TTFs are served from /fonts/ in the public folder; cached after first load.
import { jsPDF } from "jspdf";

const FONT_REGULAR = "/fonts/Roboto-Regular.ttf";
const FONT_BOLD = "/fonts/Roboto-Bold.ttf";

let cachedRegular = null;
let cachedBold = null;
let loadingPromise = null;

const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    const CHUNK = 0x8000;
    let binary = "";
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return window.btoa(binary);
};

const fetchFontBase64 = async (urls) => {
    for (const u of urls) {
        try {
            const r = await fetch(u);
            if (!r.ok) continue;
            const buf = await r.arrayBuffer();
            return arrayBufferToBase64(buf);
        } catch (_) {
            // try next
        }
    }
    throw new Error("Roboto fontu indirilemedi");
};

export const ensureTurkishFont = async () => {
    if (cachedRegular && cachedBold) return { regular: cachedRegular, bold: cachedBold };
    if (loadingPromise) return loadingPromise;
    loadingPromise = (async () => {
        const [regular, bold] = await Promise.all([
            fetchFontBase64([FONT_REGULAR]),
            fetchFontBase64([FONT_BOLD]),
        ]);
        cachedRegular = regular;
        cachedBold = bold;
        return { regular, bold };
    })();
    return loadingPromise;
};

// Pre-warm at module load (non-blocking)
if (typeof window !== "undefined") {
    ensureTurkishFont().catch(() => {});
}

// Register Roboto on a freshly created jsPDF instance and set it as default.
export const applyTurkishFont = (doc) => {
    if (!cachedRegular || !cachedBold) return false;
    try {
        doc.addFileToVFS("Roboto-Regular.ttf", cachedRegular);
        doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
        doc.addFileToVFS("Roboto-Bold.ttf", cachedBold);
        doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");
        doc.setFont("Roboto", "normal");
        return true;
    } catch (_) {
        return false;
    }
};

// Helper: create jsPDF with Turkish font applied (waits for font if not yet loaded)
export const createTurkishPdf = async (options = {}) => {
    await ensureTurkishFont().catch(() => {});
    const doc = new jsPDF(options);
    applyTurkishFont(doc);
    return doc;
};
